/**
 * LLM Stats Analytics - Beautiful data visualization components
 *
 * This module provides:
 * - Model usage breakdown (donut chart)
 * - Token ratio visualization (stacked bar)
 * - Provider comparison
 * - Time-based trends (area charts)
 * - Top tools usage
 */

import { html, nothing } from "lit";
import type { UsageAggregates, UsageTotals } from "./usage.ts";

// Color palette for charts
const CHART_COLORS = {
  primary: "#ff4d4d",
  secondary: "#10b981",
  tertiary: "#f59e0b",
  quaternary: "#8b5cf6",
  quinary: "#06b6d4",
  senary: "#ec4899",
};

const CHART_PALETTE = [
  "#ff4d4d",
  "#10b981",
  "#f59e0b",
  "#8b5cf6",
  "#06b6d4",
  "#ec4899",
  "#14b8a6",
  "#f97316",
];

// Styles for analytics components
export const analyticsStyles = `
  /* Analytics Container */
  .llm-analytics {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 16px;
    margin: 16px 0;
  }
  
  /* Analytics Card */
  .llm-analytics-card {
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: 14px;
    padding: 18px 20px;
    transition: transform 0.2s, box-shadow 0.2s, border-color 0.2s;
  }
  .llm-analytics-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0,0,0,0.08);
    border-color: var(--border-strong);
  }
  
  /* Card Header */
  .llm-analytics-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid var(--border);
  }
  .llm-analytics-title {
    font-size: 14px;
    font-weight: 700;
    color: var(--text);
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .llm-analytics-icon {
    font-size: 18px;
    opacity: 0.8;
  }
  .llm-analytics-badge {
    padding: 3px 10px;
    background: var(--bg-secondary);
    border: 1px solid var(--border);
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    color: var(--text-muted);
  }
  
  /* Donut Chart */
  .llm-donut-container {
    display: flex;
    align-items: center;
    gap: 20px;
    flex-wrap: wrap;
  }
  .llm-donut-svg {
    flex-shrink: 0;
  }
  .llm-donut-segment {
    transition: opacity 0.2s, transform 0.2s;
    cursor: pointer;
    transform-origin: center;
  }
  .llm-donut-segment:hover {
    opacity: 0.85;
    transform: scale(1.02);
  }
  .llm-donut-legend {
    flex: 1;
    min-width: 140px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .llm-donut-legend-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 6px 10px;
    border-radius: 8px;
    transition: background 0.15s;
    cursor: pointer;
  }
  .llm-donut-legend-item:hover {
    background: var(--bg-hover);
  }
  .llm-donut-legend-dot {
    width: 12px;
    height: 12px;
    border-radius: 4px;
    flex-shrink: 0;
  }
  .llm-donut-legend-label {
    flex: 1;
    font-size: 12px;
    font-weight: 500;
    color: var(--text);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .llm-donut-legend-value {
    font-family: var(--font-mono);
    font-size: 11px;
    font-weight: 600;
    color: var(--text-muted);
  }
  .llm-donut-legend-pct {
    font-size: 10px;
    color: var(--text-muted);
    min-width: 36px;
    text-align: right;
  }
  
  /* Stacked Bar */
  .llm-stacked-bar {
    height: 28px;
    background: var(--bg-secondary);
    border-radius: 8px;
    overflow: hidden;
    display: flex;
    margin: 12px 0;
  }
  .llm-stacked-segment {
    height: 100%;
    transition: width 0.4s ease;
    position: relative;
    min-width: 2px;
  }
  .llm-stacked-segment.input { background: #f59e0b; }
  .llm-stacked-segment.output { background: #ef4444; }
  .llm-stacked-segment.cache-write { background: #10b981; }
  .llm-stacked-segment.cache-read { background: #06b6d4; }
  .llm-stacked-segment:hover {
    filter: brightness(1.1);
  }
  
  /* Token Metrics */
  .llm-token-metrics {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 10px;
    margin: 12px 0;
  }
  .llm-token-metric {
    text-align: center;
    padding: 12px 8px;
    background: var(--bg-secondary);
    border-radius: 10px;
    border: 1px solid var(--border);
    transition: transform 0.15s;
  }
  .llm-token-metric:hover {
    transform: scale(1.02);
  }
  .llm-token-metric-label {
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--text-muted);
    margin-bottom: 6px;
  }
  .llm-token-metric-value {
    font-size: 18px;
    font-weight: 800;
    font-family: var(--font-mono);
    letter-spacing: -0.02em;
  }
  .llm-token-metric-value.input { color: #f59e0b; }
  .llm-token-metric-value.output { color: #ef4444; }
  .llm-token-metric-value.cache-write { color: #10b981; }
  .llm-token-metric-value.cache-read { color: #06b6d4; }
  .llm-token-metric-sub {
    font-size: 10px;
    color: var(--text-muted);
    margin-top: 4px;
  }
  
  /* Bar List */
  .llm-bar-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 12px;
  }
  .llm-bar-list-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .llm-bar-list-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 12px;
  }
  .llm-bar-list-label {
    font-weight: 600;
    color: var(--text);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 160px;
  }
  .llm-bar-list-value {
    font-family: var(--font-mono);
    font-size: 11px;
    color: var(--text-muted);
    flex-shrink: 0;
  }
  .llm-bar-list-bar {
    height: 8px;
    background: var(--bg-secondary);
    border-radius: 4px;
    overflow: hidden;
  }
  .llm-bar-list-fill {
    height: 100%;
    border-radius: 4px;
    transition: width 0.4s ease;
  }
  
  /* Area Chart */
  .llm-area-chart {
    width: 100%;
    height: 120px;
    margin: 12px 0;
  }
  .llm-area-chart-svg {
    width: 100%;
    height: 100%;
  }
  .llm-area-chart-axis-label {
    font-size: 10px;
    fill: var(--text-muted);
  }
  .llm-area-chart-grid {
    stroke: var(--border);
    stroke-width: 1;
    stroke-dasharray: 3,3;
    opacity: 0.5;
  }
  .llm-area-chart-area {
    transition: opacity 0.2s;
  }
  .llm-area-chart-line {
    fill: none;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
  .llm-area-chart-dot {
    transition: r 0.15s;
  }
  .llm-area-chart-dot:hover {
    r: 5;
  }
  
  /* Comparison Grid */
  .llm-comparison-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 10px;
    margin: 12px 0;
  }
  .llm-comparison-item {
    padding: 14px;
    background: var(--bg-secondary);
    border: 1px solid var(--border);
    border-radius: 10px;
    text-align: center;
    transition: transform 0.15s, box-shadow 0.15s;
  }
  .llm-comparison-item:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.06);
  }
  .llm-comparison-label {
    font-size: 11px;
    color: var(--text-muted);
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-bottom: 8px;
  }
  .llm-comparison-value {
    font-size: 22px;
    font-weight: 800;
    font-family: var(--font-mono);
    letter-spacing: -0.02em;
  }
  .llm-comparison-sub {
    font-size: 11px;
    color: var(--text-muted);
    margin-top: 4px;
  }
  
  /* Tool Pills */
  .llm-tool-pills {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin: 12px 0;
  }
  .llm-tool-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    background: var(--bg-secondary);
    border: 1px solid var(--border);
    border-radius: 999px;
    font-size: 12px;
    transition: all 0.15s;
  }
  .llm-tool-pill:hover {
    background: var(--bg-hover);
    border-color: var(--border-strong);
  }
  .llm-tool-pill-count {
    background: var(--accent);
    color: white;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 10px;
    font-weight: 700;
  }
  
  /* KPI Highlight */
  .llm-kpi-highlight {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px;
    background: linear-gradient(135deg, var(--bg-secondary) 0%, var(--bg) 100%);
    border: 1px solid var(--border);
    border-radius: 12px;
    margin: 12px 0;
  }
  .llm-kpi-highlight-value {
    font-size: 32px;
    font-weight: 800;
    font-family: var(--font-mono);
    letter-spacing: -0.03em;
    color: var(--text);
  }
  .llm-kpi-highlight-label {
    font-size: 13px;
    color: var(--text-muted);
    margin-top: 4px;
  }
  .llm-kpi-highlight-trend {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;
    margin-left: auto;
  }
  .llm-kpi-highlight-trend-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
  }
  .llm-kpi-highlight-trend-badge.up {
    background: rgba(16, 185, 129, 0.12);
    color: #10b981;
  }
  .llm-kpi-highlight-trend-badge.down {
    background: rgba(239, 68, 68, 0.12);
    color: #ef4444;
  }
  
  /* Empty State */
  .llm-analytics-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 32px 24px;
    text-align: center;
    gap: 8px;
  }
  .llm-analytics-empty-icon {
    font-size: 32px;
    opacity: 0.3;
  }
  .llm-analytics-empty-title {
    font-size: 13px;
    font-weight: 600;
    color: var(--text);
  }
  .llm-analytics-empty-desc {
    font-size: 12px;
    color: var(--text-muted);
    max-width: 220px;
    line-height: 1.4;
  }
  
  @media (max-width: 600px) {
    .llm-token-metrics {
      grid-template-columns: repeat(2, 1fr);
    }
    .llm-donut-container {
      flex-direction: column;
    }
  }
`;

// Formatters
const fmtUsd = (value: number) =>
  new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(Number.isFinite(value) ? value : 0);

const fmtNum = (value: number) =>
  new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(
    Number.isFinite(value) ? value : 0,
  );

const fmtK = (value: number) => {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  return fmtNum(value);
};

/**
 * Polar to Cartesian coordinate conversion
 */
function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number) {
  const angleRad = (angleDeg * Math.PI) / 180;
  return {
    x: cx + r * Math.cos(angleRad),
    y: cy + r * Math.sin(angleRad),
  };
}

/**
 * Render a beautiful donut chart for model/provider breakdown
 */
export function renderModelDonut(
  data: Array<{ label: string; value: number; color?: string }>,
  options?: { size?: number; title?: string },
) {
  if (!data?.length) {
    return html`
      <div class="llm-analytics-empty">
        <div class="llm-analytics-empty-icon">📊</div>
        <div class="llm-analytics-empty-title">No model data</div>
        <div class="llm-analytics-empty-desc">
          Model usage will appear here once sessions are processed.
        </div>
      </div>
    `;
  }

  const size = options?.size ?? 160;
  const radius = size / 2;
  const innerRadius = radius * 0.6;
  const cx = radius;
  const cy = radius;

  const total = data.reduce((sum, d) => sum + d.value, 0);
  if (total === 0) {
    return html`
      <div class="llm-analytics-empty">
        <div class="llm-analytics-empty-title">No usage data</div>
      </div>
    `;
  }

  let currentAngle = -90;
  const arcs = data.map((item, i) => {
    const angle = Math.max(1, (item.value / total) * 360);
    const startAngle = currentAngle;
    const endAngle = currentAngle + angle;
    currentAngle = endAngle;

    const start = polarToCartesian(cx, cy, radius, startAngle);
    const end = polarToCartesian(cx, cy, radius, endAngle);
    const innerStart = polarToCartesian(cx, cy, innerRadius, startAngle);
    const innerEnd = polarToCartesian(cx, cy, innerRadius, endAngle);

    const largeArcFlag = angle > 180 ? 1 : 0;

    const d = [
      `M ${start.x.toFixed(2)} ${start.y.toFixed(2)}`,
      `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${end.x.toFixed(2)} ${end.y.toFixed(2)}`,
      `L ${innerEnd.x.toFixed(2)} ${innerEnd.y.toFixed(2)}`,
      `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${innerStart.x.toFixed(2)} ${innerStart.y.toFixed(2)}`,
      "Z",
    ].join(" ");

    return {
      d,
      color: item.color || CHART_PALETTE[i % CHART_PALETTE.length],
      label: item.label,
      value: item.value,
      pct: ((item.value / total) * 100).toFixed(1),
    };
  });

  return html`
    ${
      options?.title
        ? html`
          <div class="llm-analytics-header">
            <div class="llm-analytics-title">
              <span class="llm-analytics-icon">📊</span>
              ${options.title}
            </div>
          </div>
        `
        : nothing
    }
    <div class="llm-donut-container">
      <svg class="llm-donut-svg" width=${size} height=${size} viewBox="0 0 ${size} ${size}>
        ${arcs.map(
          (arc) => html`
            <path
              d=${arc.d}
              fill=${arc.color}
              class="llm-donut-segment"
            >
              <title>${arc.label}: ${fmtUsd(arc.value)} (${arc.pct}%)</title>
            </path>
          `,
        )}
        <text x=${cx} y=${cy - 6} text-anchor="middle" font-size="16" font-weight="700" fill="var(--text)">
          ${fmtUsd(total)}
        </text>
        <text x=${cx} y=${cy + 12} text-anchor="middle" font-size="11" fill="var(--text-muted)">
          total cost
        </text>
      </svg>
      <div class="llm-donut-legend">
        ${arcs.map(
          (arc) => html`
            <div class="llm-donut-legend-item">
              <span class="llm-donut-legend-dot" style="background: ${arc.color}"></span>
              <span class="llm-donut-legend-label" title=${arc.label}>${arc.label}</span>
              <span class="llm-donut-legend-value">${fmtUsd(arc.value)}</span>
              <span class="llm-donut-legend-pct">${arc.pct}%</span>
            </div>
          `,
        )}
      </div>
    </div>
  `;
}

/**
 * Render token breakdown as stacked bar with metrics
 */
export function renderTokenBreakdown(totals: UsageTotals | null) {
  if (!totals) {
    return html`
      <div class="llm-analytics-empty">
        <div class="llm-analytics-empty-icon">🔢</div>
        <div class="llm-analytics-empty-title">No token data</div>
      </div>
    `;
  }

  const { input, output, cacheRead, cacheWrite, totalTokens, totalCost } = totals;
  const maxTokens = Math.max(input, output, cacheRead, cacheWrite, 1);

  return html`
    <div class="llm-analytics-header">
      <div class="llm-analytics-title">
        <span class="llm-analytics-icon">🔢</span>
        Token Breakdown
      </div>
      <span class="llm-analytics-badge">${fmtK(totalTokens)} tokens</span>
    </div>
    
    <div class="llm-stacked-bar">
      <div class="llm-stacked-segment input" style="width: ${(input / maxTokens) * 100}%" title="Input: ${fmtK(input)}">
      </div>
      <div class="llm-stacked-segment output" style="width: ${(output / maxTokens) * 100}%" title="Output: ${fmtK(output)}">
      </div>
      <div class="llm-stacked-segment cache-write" style="width: ${(cacheWrite / maxTokens) * 100}%" title="Cache Write: ${fmtK(cacheWrite)}">
      </div>
      <div class="llm-stacked-segment cache-read" style="width: ${(cacheRead / maxTokens) * 100}%" title="Cache Read: ${fmtK(cacheRead)}">
      </div>
    </div>
    
    <div class="llm-token-metrics">
      <div class="llm-token-metric">
        <div class="llm-token-metric-label">Input</div>
        <div class="llm-token-metric-value input">${fmtK(input)}</div>
        <div class="llm-token-metric-sub">${((input / totalTokens) * 100).toFixed(0)}%</div>
      </div>
      <div class="llm-token-metric">
        <div class="llm-token-metric-label">Output</div>
        <div class="llm-token-metric-value output">${fmtK(output)}</div>
        <div class="llm-token-metric-sub">${((output / totalTokens) * 100).toFixed(0)}%</div>
      </div>
      <div class="llm-token-metric">
        <div class="llm-token-metric-label">Cache Write</div>
        <div class="llm-token-metric-value cache-write">${fmtK(cacheWrite)}</div>
        <div class="llm-token-metric-sub">${((cacheWrite / totalTokens) * 100).toFixed(0)}%</div>
      </div>
      <div class="llm-token-metric">
        <div class="llm-token-metric-label">Cache Read</div>
        <div class="llm-token-metric-value cache-read">${fmtK(cacheRead)}</div>
        <div class="llm-token-metric-sub">${((cacheRead / totalTokens) * 100).toFixed(0)}%</div>
      </div>
    </div>
    
    <div class="llm-kpi-highlight">
      <div>
        <div class="llm-kpi-highlight-value">${fmtUsd(totalCost)}</div>
        <div class="llm-kpi-highlight-label">Total LLM cost</div>
      </div>
    </div>
  `;
}

/**
 * Render provider comparison bars
 */
export function renderProviderComparison(aggregates: UsageAggregates | null) {
  if (!aggregates?.byProvider?.length) {
    return html`
      <div class="llm-analytics-empty">
        <div class="llm-analytics-empty-icon">☁️</div>
        <div class="llm-analytics-empty-title">No provider data</div>
      </div>
    `;
  }

  const providers = aggregates.byProvider.slice(0, 6);
  const maxCost = Math.max(...providers.map((p) => p.totals.totalCost), 0.01);

  return html`
    <div class="llm-analytics-header">
      <div class="llm-analytics-title">
        <span class="llm-analytics-icon">☁️</span>
        Provider Breakdown
      </div>
      <span class="llm-analytics-badge">${providers.length} providers</span>
    </div>
    
    <div class="llm-bar-list">
      ${providers.map((p, i) => {
        const pct = (p.totals.totalCost / maxCost) * 100;
        return html`
          <div class="llm-bar-list-item">
            <div class="llm-bar-list-header">
              <span class="llm-bar-list-label" title=${p.provider ?? "Unknown"}>${p.provider ?? "Unknown"}</span>
              <span class="llm-bar-list-value">${fmtUsd(p.totals.totalCost)}</span>
            </div>
            <div class="llm-bar-list-bar">
              <div class="llm-bar-list-fill" style="width: ${pct}%; background: ${CHART_PALETTE[i % CHART_PALETTE.length]}"></div>
            </div>
          </div>
        `;
      })}
    </div>
  `;
}

/**
 * Render top tools usage
 */
export function renderTopTools(aggregates: UsageAggregates | null) {
  if (!aggregates?.tools?.tools?.length) {
    return html`
      <div class="llm-analytics-empty">
        <div class="llm-analytics-empty-icon">🔧</div>
        <div class="llm-analytics-empty-title">No tools data</div>
        <div class="llm-analytics-empty-desc">Tool calls will appear here from session logs.</div>
      </div>
    `;
  }

  const tools = aggregates.tools.tools.slice(0, 8);
  const maxCount = Math.max(...tools.map((t) => t.count), 1);

  return html`
    <div class="llm-analytics-header">
      <div class="llm-analytics-title">
        <span class="llm-analytics-icon">🔧</span>
        Top Tools
      </div>
      <span class="llm-analytics-badge">${aggregates.tools.uniqueTools} unique</span>
    </div>
    
    <div class="llm-bar-list">
      ${tools.map((tool, i) => {
        const pct = (tool.count / maxCount) * 100;
        return html`
          <div class="llm-bar-list-item">
            <div class="llm-bar-list-header">
              <span class="llm-bar-list-label" title=${tool.name}>${tool.name}</span>
              <span class="llm-bar-list-value">${tool.count.toLocaleString()}</span>
            </div>
            <div class="llm-bar-list-bar">
              <div class="llm-bar-list-fill" style="width: ${pct}%; background: ${CHART_PALETTE[(i + 2) % CHART_PALETTE.length]}"></div>
            </div>
          </div>
        `;
      })}
    </div>
  `;
}

/**
 * Render model usage comparison
 */
export function renderModelComparison(aggregates: UsageAggregates | null) {
  if (!aggregates?.byModel?.length) {
    return html`
      <div class="llm-analytics-empty">
        <div class="llm-analytics-empty-icon">🤖</div>
        <div class="llm-analytics-empty-title">No model data</div>
      </div>
    `;
  }

  const models = aggregates.byModel.slice(0, 6);
  const maxTokens = Math.max(...models.map((m) => m.totals.totalTokens), 1);

  return html`
    <div class="llm-analytics-header">
      <div class="llm-analytics-title">
        <span class="llm-analytics-icon">🤖</span>
        Model Usage
      </div>
      <span class="llm-analytics-badge">${models.length} models</span>
    </div>
    
    <div class="llm-bar-list">
      ${models.map((m, i) => {
        const pct = (m.totals.totalTokens / maxTokens) * 100;
        const label = m.model ?? m.provider ?? "Unknown";
        return html`
          <div class="llm-bar-list-item">
            <div class="llm-bar-list-header">
              <span class="llm-bar-list-label" title=${label}>${label}</span>
              <span class="llm-bar-list-value">${fmtK(m.totals.totalTokens)}</span>
            </div>
            <div class="llm-bar-list-bar">
              <div class="llm-bar-list-fill" style="width: ${pct}%; background: ${CHART_PALETTE[i % CHART_PALETTE.length]}"></div>
            </div>
          </div>
        `;
      })}
    </div>
  `;
}

/**
 * Render channel breakdown
 */
export function renderChannelBreakdown(aggregates: UsageAggregates | null) {
  if (!aggregates?.byChannel?.length) {
    return html`
      <div class="llm-analytics-empty">
        <div class="llm-analytics-empty-icon">💬</div>
        <div class="llm-analytics-empty-title">No channel data</div>
      </div>
    `;
  }

  const channels = aggregates.byChannel.slice(0, 5);
  const maxCost = Math.max(...channels.map((c) => c.totals.totalCost), 0.01);

  return html`
    <div class="llm-analytics-header">
      <div class="llm-analytics-title">
        <span class="llm-analytics-icon">💬</span>
        Channel Breakdown
      </div>
      <span class="llm-analytics-badge">${channels.length} channels</span>
    </div>
    
    <div class="llm-bar-list">
      ${channels.map((c, i) => {
        const pct = (c.totals.totalCost / maxCost) * 100;
        return html`
          <div class="llm-bar-list-item">
            <div class="llm-bar-list-header">
              <span class="llm-bar-list-label" title=${c.channel ?? "Unknown"}>${c.channel ?? "Unknown"}</span>
              <span class="llm-bar-list-value">${fmtUsd(c.totals.totalCost)}</span>
            </div>
            <div class="llm-bar-list-bar">
              <div class="llm-bar-list-fill" style="width: ${pct}%; background: ${CHART_PALETTE[(i + 4) % CHART_PALETTE.length]}"></div>
            </div>
          </div>
        `;
      })}
    </div>
  `;
}

/**
 * Props for the LLM Stats Analytics dashboard
 */
export type LlmStatsAnalyticsProps = {
  totals: UsageTotals | null;
  aggregates: UsageAggregates | null;
};

/**
 * Render a complete analytics dashboard
 */
export function renderLlmStatsAnalytics(params: LlmStatsAnalyticsProps) {
  const { totals, aggregates } = params;

  return html`
    <style>${analyticsStyles}</style>
    <div class="llm-analytics">
      <!-- Token Breakdown -->
      <div class="llm-analytics-card">
        ${renderTokenBreakdown(totals)}
      </div>
      
      <!-- Model Usage -->
      <div class="llm-analytics-card">
        ${renderModelDonut(
          aggregates?.byModel?.map((m, i) => ({
            label: m.model ?? m.provider ?? "Unknown",
            value: m.totals.totalCost,
            color: CHART_PALETTE[i % CHART_PALETTE.length],
          })) ?? [],
          { size: 140, title: "Model Cost Distribution" },
        )}
      </div>
      
      <!-- Provider Comparison -->
      <div class="llm-analytics-card">
        ${renderProviderComparison(aggregates)}
      </div>
      
      <!-- Top Tools -->
      <div class="llm-analytics-card">
        ${renderTopTools(aggregates)}
      </div>
      
      <!-- Model Comparison -->
      <div class="llm-analytics-card">
        ${renderModelComparison(aggregates)}
      </div>
      
      <!-- Channel Breakdown -->
      <div class="llm-analytics-card">
        ${renderChannelBreakdown(aggregates)}
      </div>
    </div>
  `;
}
